import useEntityState from "./../entityTemplateReducer";
import memeService from "./../MemeService";
import notificationService from "./../NotificationService";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

// ============================ USAGE NOTES ============================
//
// 1. **Load Templates** -> with API call
//    - Start by calling either of the below to load the templates.
//      - `await loadSelectedTemplates`
//      - `await loadUploadedTemplates`
//      - `await loadMostRecentMemes`
//
// 2. **Access Templates**
//    - Once loaded, access templates using:
//      - `getSelectedTemplates` for selected templates returns [template, {..}]
//      - `getUploadedTemplates` for uploaded templates returns [template, {..}]
//      - `getMostRecentMemes`   for memes templates returns [meme, {..}]
//
// 3. **Re-render Templates (React)**
//    - To trigger a re-render in React, call:
//      - `replaceSelectedTemplate` for selected templates
//      - `replaceUploadedTemplate` for uploaded templates
//      - `replaceMostRecentMeme`   for most recent templates
//
// 4. **Add a New Template** -> with API call
//    - To add a new template, use:
//      - `await addFirstSelectedTemplate` for selected templates
//      - `await addFirstUploadedTemplate` for uploaded templates
//
// 5. **Delete a Template** -> with API call
//    - To delete a template, use:
//      - `await deleteSelectedTemplate` for selected templates
//      - `await deleteUploadedTemplate` for uploaded templates
//      - `await deleteMostRecentMeme`   for most recent memes
//
// 6. **Overwrite Template Array**
//    - To overwrite the current template list, use:
//      - `setSelectedTemplates` for selected templates
//      - `setUploadedTemplates` for uploaded templates
//      - `setMostRecentMemes`   for most recent memes
//
// =======================================================================

const PreloadTemplateService = () => {
    const { t } = useTranslation("preloadErrors");

    const {
        getEntities,
        deleteEntityState,
        setEntityState,
        updateEntityState,
        addEntityState,
        addFirstEntityState,
    } = useEntityState();

    // Selected templates //////////////

    const selectedTemplates = useMemo(() => {
        return getEntities("selectedTemplates");
    }, [getEntities]);

    const setSelectedTemplates = useCallback(
        (templates) => {
            if (!templates)
                return console.warn(
                    "PreloadTemplateService: setSelectedTemplates missing templates",
                );

            // Do not use this to update 1 template
            setEntityState(templates, "selectedTemplates");
        },
        [setEntityState],
    );

    const replaceSelectedTemplate = useCallback(
        (template) => {
            if (!template)
                return console.warn(
                    "PreloadTemplateService: replaceSelectedTemplate missing template",
                );
            updateEntityState(template, "selectedTemplates");
        },
        [updateEntityState],
    );

    const updateSelectedTemplate = useCallback(
        async (template, price, name) => {
            if (!template)
                return console.warn(
                    "PreloadTemplateService: updateSelectedTemplate missing template",
                );

            try {
                if (isNaN(price)) {
                    notificationService.setMessage(
                        t("templatesSelectedInvalidBits"),
                    );
                    return;
                }

                if (!name?.length) {
                    notificationService.setMessage(
                        t("templatesSelectedNoName"),
                    );
                    return;
                }

                if (name.length > 50) {
                    notificationService.setMessage(
                        t("templatesSelectedNameIsTooLong"),
                    );
                    return;
                }

                const response = await memeService.updateSelectedTemplate(
                    template.id,
                    price,
                    name,
                );
                if (!response.ok)
                    throw new Error("Could not update selected template!");

                template.price = price ? price : template.price;
                template.name = name ? name : template.name;
                replaceSelectedTemplate(template);
            } catch (error) {
                notificationService.setMessage(
                    t("templatesSelectedErrorUpdating"),
                );
                console.error(
                    "PreloadTemplateService: updateSelectedTemplate ->",
                    error,
                );
                return;
            }
        },
        [replaceSelectedTemplate],
    );

    const selectedTemplatesAddLogic = async (template, price) => {
        if (!template)
            return console.warn(
                "PreloadTemplateService: addSelectedTemplate missing template",
            );

        let addedTemplate = {};

        try {
            // "id" is a hashed identifier for internal use, while "templateId" is a public UUID used for search.
            // This distinction prevents exposing sensitive internal structure and protects against unauthorized actions.
            addedTemplate = await memeService.addSelectedTemplates(
                price ? price : "0",
                template.id ? template.id : template.templateId,
                template.title,
            );
            if (!addedTemplate.id) throw new Error("NO TEMPLATE ID");
        } catch (error) {
            notificationService.setMessage(t("templatesSelectedErrorAdding"));
            console.error(
                "PreloadTemplateService: addSelectedTemplate ->",
                error,
            );
            return false;
        }

        if (addedTemplate.message) {
            notificationService.setMessage(t("templatesSelectedMaxNumber"));
            return false;
        }

        return addedTemplate;
    };

    const pushSelectedTemplates = useCallback(
        async (template, price) => {
            const addedTemplate = await selectedTemplatesAddLogic(
                template,
                price,
            );

            if (addedTemplate)
                addEntityState(addedTemplate, "selectedTemplates");

            return addedTemplate;
        },
        [addEntityState],
    );

    const addFirstSelectedTemplate = useCallback(
        async (template, price) => {
            const addedTemplate = await selectedTemplatesAddLogic(
                template,
                price,
            );

            if (addedTemplate)
                addFirstEntityState(addedTemplate, "selectedTemplates");

            return addedTemplate;
        },
        [addFirstEntityState],
    );

    const deleteSelectedTemplate = useCallback(
        async (template) => {
            if (!template || !template.id)
                return console.warn(
                    "PreloadTemplateService: deleteSelectedTemplate missing template",
                );

            try {
                const response = await memeService.deleteSelectedTemplate(
                    template.id,
                );
                if (!response.ok) throw new Error("Response was not ok");
            } catch (error) {
                notificationService.setMessage(
                    t("templatesSelectedErrorUnselecting"),
                );
                console.error(
                    "PreloadTemplateService: deleteSelectedTemplate ->",
                    error,
                );
                return false;
            }

            deleteEntityState(template, "selectedTemplates");
            return true;
        },
        [deleteEntityState],
    );

    const loadSelectedTemplates = useCallback(async () => {
        return await loadTemplates(
            memeService.listSelectedTemplates.bind(memeService),
            setSelectedTemplates,
        );
    }, [setSelectedTemplates]);

    // Uploaded templates //////////////
    const uploadedTemplates = useMemo(() => {
        return getEntities("uploadedTemplates");
    }, [getEntities]);

    const setUploadedTemplates = useCallback(
        (templates) => {
            if (!templates)
                return console.warn(
                    "PreloadTemplateService: setUploadedTemplates missing templates",
                );

            // Do not use this to update 1 template
            setEntityState(templates, "uploadedTemplates");
        },
        [setEntityState],
    );

    const replaceUploadedTemplate = useCallback(
        (template) => {
            if (!template)
                return console.warn(
                    "PreloadTemplateService: replaceUploadedTemplate missing template",
                );
            updateEntityState(template, "uploadedTemplates");
        },
        [updateEntityState],
    );

    const uploadedTemplatesAddLogic = async (
        title,
        isPublicUpload,
        uploadFileData,
    ) => {
        if (!title || !uploadFileData)
            return console.warn(
                "PreloadTemplateService: addUploadedTemplate missing uploadFileData or title",
            );

        let newTemplate = {};

        try {
            newTemplate = await memeService.uploadTemplate(
                title,
                isPublicUpload ? isPublicUpload : false,
                uploadFileData,
            );
            if (!newTemplate.template && newTemplate.message) {
                notificationService.setMessage(
                    t("uploadTemplateMaxLimitReached"),
                );
                return false;
            } else if (!newTemplate.template) {
                notificationService.setMessage(t("uploadTemplateImageTooBig"));
                return false;
            }

            return newTemplate.template;
        } catch (error) {
            notificationService.setMessage(t("uploadTemplateErrorUploading"));
            console.error(
                "PreloadTemplateService: addUploadedTemplate ->",
                error,
            );
            return false;
        }
    };

    const pushUploadedTemplate = useCallback(
        async (title, isPublicUpload, uploadFileData) => {
            const template = await uploadedTemplatesAddLogic(
                title,
                isPublicUpload,
                uploadFileData,
            );

            if (template) addEntityState(template, "uploadedTemplates");
            return template;
        },
        [addEntityState],
    );

    const addFirstUploadedTemplate = useCallback(
        async (title, isPublicUpload, uploadFileData) => {
            const template = await uploadedTemplatesAddLogic(
                title,
                isPublicUpload,
                uploadFileData,
            );

            if (template) addFirstEntityState(template, "uploadedTemplates");
            return template;
        },
        [addFirstEntityState],
    );

    const deleteUploadedTemplate = useCallback(
        async (template) => {
            if (!template)
                return console.warn(
                    "PreloadTemplateService: deleteUploadedTemplate missing template",
                );

            try {
                const response = await memeService.deleteUploadedTemplate(
                    template.id,
                );
                if (!response.ok) throw new Error("Response was not ok!");
            } catch (error) {
                notificationService.setMessage(
                    t("uploadedTemplateErrorDeleting"),
                );
                console.error(
                    "PreloadTemplateService: deleteUploadedTemplate ->",
                    error,
                );
                return false;
            }

            deleteEntityState(template, "uploadedTemplates");
            return true;
        },
        [deleteEntityState],
    );

    const loadUploadedTemplates = useCallback(async () => {
        return await loadTemplates(
            memeService.listUploadedTemplates.bind(memeService),
            setUploadedTemplates,
        );
    }, [setUploadedTemplates]);

    // Most recent memes templates //////////////
    const mostRecentMemes = useMemo(() => {
        return getEntities("mostRecentMemes");
    }, [getEntities]);

    const setMostRecentMemes = useCallback(
        (templates) => {
            if (!templates)
                return console.warn(
                    "PreloadTemplateService: setMostRecentMemes missing templates",
                );

            // Do not use this to update 1 template
            setEntityState(templates, "mostRecentMemes");
        },
        [setEntityState],
    );

    const replaceMostRecentMeme = useCallback(
        (template) => {
            if (!template)
                return console.warn(
                    "PreloadTemplateService: replaceMostRecentMeme missing template",
                );
            updateEntityState(template, "mostRecentMemes");
        },
        [updateEntityState],
    );

    const deleteMostRecentMeme = useCallback(
        async (template) => {
            if (!template)
                return console.warn(
                    "PreloadTemplateService: deleteMostRecentMeme missing template",
                );

            try {
                const response = await memeService.deleteMostRecentMeme(
                    template.id,
                );
                if (response) {
                    deleteEntityState(template, "mostRecentMemes");
                    return true;
                }
            } catch (error) {
                notificationService.setMessage(
                    t("mostRecentMemeErrorDeleting"),
                );
                console.error(
                    "PreloadTemplateService: deleteMostRecentMeme ->",
                    error,
                );
            }

            return false;
        },
        [deleteEntityState],
    );

    const loadMostRecentMemes = useCallback(async () => {
        return await loadTemplates(
            memeService.listMostRecentMemes.bind(memeService),
            setMostRecentMemes,
            "memes",
            true,
        ); // true = pagination disabled
    }, [setMostRecentMemes]);

    const pendingMemes = useMemo(() => {
        return getEntities("pendingMemes");
    }, [getEntities]);

    const setListOfPendingMemes = useCallback(
        (templates) => {
            if (!templates)
                return console.warn(
                    "PreloadTemplateService: setListOfPendingMemes missing meme",
                );

            // Do not use this to update 1 template
            setEntityState(templates, "pendingMemes");
        },
        [setEntityState],
    );

    const replacePendingMeme = useCallback(
        (template) => {
            if (!template)
                return console.warn(
                    "PreloadTemplateService: replacePendingMeme missing meme",
                );
            updateEntityState(template, "pendingMemes");
        },
        [updateEntityState],
    );

    const approveOrDenyMeme = useCallback(
        async (template, state) => {
            if (!template)
                return console.warn(
                    "PreloadTemplateService: deletePendingMeme missing meme",
                );
            if (state !== "DENY" && state !== "APPROVE")
                return console.warn(
                    "PreloadTemplateService: deletePendingMeme missing wrong state:",
                    state,
                );

            try {
                const response = await memeService.approveOrDenyMeme(
                    state,
                    template.id,
                );
                if (response.ok) deleteEntityState(template, "pendingMemes");
                else if (response.errorCode)
                    notificationService.setMessage(
                        t("setMEssageToDisplaySourceNotActive"),
                    );
            } catch (error) {
                notificationService.setMessage(
                    t("approveDenyMemeErrorDeleting"),
                );
                console.error(
                    "PreloadTemplateService: deletePendingMeme ->",
                    error,
                );
                return;
            }
        },
        [deleteEntityState],
    );

    const deletePendingMeme = useCallback(
        (template) => {
            if (!template)
                return console.warn(
                    "PreloadTemplateService: deletePendingMeme missing meme",
                );
            deleteEntityState(template, "pendingMemes");
        },
        [deleteEntityState],
    );

    const pushPendingMeme = useCallback(
        (template) => {
            if (!template)
                return console.warn(
                    "PreloadTemplateService: addPendingMeme missing meme",
                );

            addEntityState(template, "pendingMemes");

            return template;
        },
        [addEntityState],
    );

    const addFirstPendingMeme = useCallback(
        (template) => {
            if (!template)
                return console.warn(
                    "PreloadTemplateService: addPendingMeme missing meme",
                );

            addFirstEntityState(template, "pendingMemes");

            return template;
        },
        [addFirstEntityState],
    );

    const loadListOfPendingApproval = useCallback(async () => {
        return await loadTemplates(
            memeService.listPendingApprovalMemes.bind(memeService),
            setListOfPendingMemes,
            "memes",
        );
    }, [setListOfPendingMemes]);

    const loadTemplates = async (
        serviceFunction,
        setTemplatesState,
        responseObjectName,
        disablePagination,
    ) => {
        let templateAccumulationMap = [];
        let paginationToken = null;

        try {
            // Start loading the templates
            do {
                const responseObject = await serviceFunction(paginationToken);

                const responseObjectSelector = responseObjectName
                    ? responseObject[responseObjectName]
                    : responseObject.templates;

                // Accumulate templates
                templateAccumulationMap = [
                    ...templateAccumulationMap,
                    ...responseObjectSelector,
                ];

                // Update the state once with all the templates
                setTemplatesState(templateAccumulationMap);

                // Set the next pagination token if exists
                paginationToken = disablePagination
                    ? null
                    : responseObject.paginationToken;
            } while (paginationToken);
        } catch (error) {
            // In case of an error, notify the user and set the empty state
            notificationService.setMessage(t("templatesLodingError"));
            console.error(
                "PreloadTemplateService: loadTemplates could not load memes !",
                error,
            );
            setTemplatesState(templateAccumulationMap);
        }

        return templateAccumulationMap;
    };

    return {
        selectedTemplates,
        replaceSelectedTemplate,
        updateSelectedTemplate,
        addFirstSelectedTemplate,
        pushSelectedTemplates,
        deleteSelectedTemplate,
        loadSelectedTemplates,
        uploadedTemplates,
        replaceUploadedTemplate,
        addFirstUploadedTemplate,
        pushUploadedTemplate,
        deleteUploadedTemplate,
        loadUploadedTemplates,
        mostRecentMemes,
        replaceMostRecentMeme,
        deleteMostRecentMeme,
        loadMostRecentMemes,
        pendingMemes,
        replacePendingMeme,
        approveOrDenyMeme,
        addFirstPendingMeme,
        pushPendingMeme,
        deletePendingMeme,
        loadListOfPendingApproval,
    };
};

export default PreloadTemplateService;
